/**
 * The codes with which the API refuses the session itself, as opposed to
 * refusing something the session asked for.
 *
 * A closed list on purpose. The API also answers 401 for a *live* session whose
 * email no longer passes a project's domain rules (`ERR_INVALID_DOMAIN`,
 * `ERR_INVALID_EMAIL`) and for wrong credentials on the sign-in calls — none of
 * which mean the stored token is dead, and dropping it on those would sign
 * people out for the wrong reason.
 */
export const SESSION_REJECTED_CODES: ReadonlySet<string> = new Set([
  "ERR_UNAUTHORIZED",
]);

/**
 * Whether a failed response says that the session this app holds is no longer
 * accepted — so the token should be dropped and the visitor asked to sign in
 * again, rather than shown an error about the content.
 *
 * Two checks beyond the status and the code guard against the false positive
 * that matters most: the request must have gone out with *this* session's
 * bearer token. The MFA and account-setup screens send an explicit header for a
 * different, short-lived token, and a 401 on those must leave the real session
 * alone.
 */
export function isRejectedSessionResponse(
  err: unknown,
  currentTokenId: string | undefined
): boolean {
  if (!currentTokenId) return false;

  const error = err as AxiosLikeError | undefined;
  const response = error?.response;
  if (response?.status !== 401) return false;

  const config = error?.config ?? response?.config;
  // Only requests made through the app's own API clients carry this flag.
  if (!config?.clientRequest) return false;

  if (!SESSION_REJECTED_CODES.has(response?.data?.message ?? "")) return false;

  return authorizationOf(config) === `Bearer ${currentTokenId}`;
}

interface AxiosLikeError {
  config?: RequestConfigLike;
  response?: {
    status?: number;
    data?: { message?: string };
    config?: RequestConfigLike;
  };
}

interface RequestConfigLike {
  clientRequest?: boolean;
  headers?:
    | Record<string, unknown>
    | { get(name: string): unknown }
    | undefined;
}

/** The request's bearer header, whichever shape axios handed it back in. */
function authorizationOf(config: RequestConfigLike): string | undefined {
  const headers = config.headers;
  if (!headers) return undefined;

  const value =
    "get" in headers && typeof headers.get === "function"
      ? headers.get("Authorization") ?? headers.get("authorization")
      : (headers as Record<string, unknown>).Authorization ??
        (headers as Record<string, unknown>).authorization;

  return typeof value === "string" ? value : undefined;
}
